import React, { useState, useEffect, useMemo } from 'react'
import Swal from 'sweetalert2'
import Select from 'react-select'
import apiClient, { API_ENDPOINTS, getUserInfoSync } from '../../config/api'

const selectStyles = {
  control: (base) => ({
    ...base,
    minHeight: '38px',
    borderColor: '#ced4da',
    '&:hover': {
      borderColor: '#ced4da'
    }
  })
}

const getBranchId = (userInfo) => {
  if (!userInfo) return null

  if (typeof userInfo.branch === 'number') {
    return userInfo.branch
  }

  if (typeof userInfo.branch === 'object' && userInfo.branch !== null) {
    if (typeof userInfo.branch.id === 'number') {
      return userInfo.branch.id
    }
  }

  return (
    userInfo.branch_id ||
    userInfo.assigned_branch_id ||
    userInfo.branchId ||
    null
  )
}

const parseApiError = (error, fallback) => {
  if (!error.response?.data) {
    return fallback
  }

  const errorData = error.response.data

  if (errorData.detail) {
    return typeof errorData.detail === 'string' ? errorData.detail : JSON.stringify(errorData.detail)
  }
  if (errorData.non_field_errors) {
    return Array.isArray(errorData.non_field_errors)
      ? errorData.non_field_errors[0]
      : errorData.non_field_errors
  }
  if (errorData.message) {
    return errorData.message
  }

  const firstError = Object.values(errorData)[0]
  return Array.isArray(firstError) ? firstError[0] : firstError || fallback
}

const emptyItem = () => ({
  product_id: '',
  quantity: '',
  reorder_level: '',
  selling_price: '',
  supplier_id: '',
  notes: ''
})

const CashierRequestStock = () => {
  const userInfo = useMemo(() => getUserInfoSync() || null, [])
  const branchId = useMemo(() => getBranchId(userInfo), [userInfo])

  const [branchContext, setBranchContext] = useState(null)
  const [items, setItems] = useState([emptyItem()])
  const [notes, setNotes] = useState('')
  const [products, setProducts] = useState([])
  const [suppliers, setSuppliers] = useState([])
  const [loading, setLoading] = useState(false)
  const [fetchingData, setFetchingData] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        setFetchingData(true)

        const requests = [
          apiClient.get('/api/products/products/'),
          apiClient.get(API_ENDPOINTS.SUPPLIERS)
        ]

        if (branchId) {
          requests.push(apiClient.get(API_ENDPOINTS.BRANCHES))
        }

        const responses = await Promise.all(requests)
        const [productsResponse, suppliersResponse, branchesResponse] = responses

        setProducts(productsResponse.data || [])
        setSuppliers(suppliersResponse.data || [])

        if (branchId && branchesResponse) {
          const branches = branchesResponse.data?.results || branchesResponse.data || []
          const branch = branches.find((entry) => entry.id === branchId)

          if (branch) {
            const warehouseId = branch.warehouse ?? branch.warehouse_id ?? null
            setBranchContext({
              branchId: branch.id,
              branchName: branch.name || userInfo?.branch_name || 'Your Branch',
              warehouseId,
              warehouseName: branch.warehouse_name || userInfo?.warehouse_name || 'Linked Warehouse'
            })
          } else {
            setBranchContext({
              branchId,
              branchName: userInfo?.branch_name || 'Your Branch',
              warehouseId: null,
              warehouseName: userInfo?.warehouse_name || 'Linked Warehouse'
            })
          }
        }
      } catch (error) {
        console.error('Error fetching stock request data:', error)
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: parseApiError(error, 'Failed to load products and branch details. Please refresh the page.')
        })
      } finally {
        setFetchingData(false)
      }
    }

    fetchData()
  }, [branchId, userInfo?.branch_name, userInfo?.warehouse_name])

  const productOptions = useMemo(
    () =>
      products.map((product) => ({
        value: product.id,
        label: `${product.name} (${product.sku})`
      })),
    [products]
  )

  const supplierOptions = useMemo(
    () =>
      suppliers.map((supplier) => ({
        value: supplier.id,
        label: supplier.name
      })),
    [suppliers]
  )

  const handleItemChange = (index, field, value) => {
    const nextItems = [...items]
    nextItems[index] = {
      ...nextItems[index],
      [field]: value === '' ? '' : value
    }
    setItems(nextItems)
  }

  const handleItemSelectChange = (index, selectedOption, fieldName) => {
    const nextItems = [...items]
    nextItems[index] = {
      ...nextItems[index],
      [fieldName]: selectedOption ? selectedOption.value.toString() : ''
    }
    setItems(nextItems)
  }

  const addItem = () => {
    setItems([...items, emptyItem()])
  }

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!branchId) {
      Swal.fire({
        icon: 'error',
        title: 'Branch Required',
        text: 'You are not assigned to a branch. Please contact your administrator.'
      })
      return
    }

    if (!branchContext?.warehouseId) {
      Swal.fire({
        icon: 'error',
        title: 'Warehouse Required',
        text: 'Could not determine the source warehouse for your branch. Please contact your administrator.'
      })
      return
    }

    const validItems = items.filter(
      (item) => item.product_id && item.quantity && item.reorder_level !== ''
    )

    if (validItems.length === 0) {
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        text: 'Please add at least one product with quantity and reorder level.'
      })
      return
    }

    setLoading(true)

    try {
      const transfers = validItems.map((item) => {
        const transfer = {
          transfer_type: 'warehouse_to_branch',
          source_warehouse_id: parseInt(branchContext.warehouseId, 10),
          destination_branch_id: parseInt(branchId, 10),
          product_id: parseInt(item.product_id, 10),
          quantity: parseFloat(item.quantity),
          reorder_level: parseFloat(item.reorder_level)
        }

        if (item.selling_price !== '' && item.selling_price !== null) {
          transfer.selling_price = parseFloat(item.selling_price)
        }

        if (item.supplier_id) {
          transfer.supplier_id = parseInt(item.supplier_id, 10)
        }

        const itemNotes = item.notes?.trim()
        if (itemNotes) {
          transfer.notes = itemNotes
        } else if (notes.trim()) {
          transfer.notes = notes.trim()
        }

        return transfer
      })

      const response = await apiClient.post(API_ENDPOINTS.STOCK_TRANSFERS_BULK, {
        transfers
      })

      const summary = response.data?.summary
      const created = response.data?.created || []
      const errors = response.data?.errors || []

      if (summary?.failed > 0 && summary?.successful > 0) {
        const errorDetails = errors
          .map((entry) => entry.error)
          .filter(Boolean)
          .slice(0, 3)
          .join('\n')

        Swal.fire({
          icon: 'warning',
          title: 'Partially Submitted',
          text: `${summary.successful} of ${summary.total} stock request(s) were created.${errorDetails ? `\n\n${errorDetails}` : ''}`,
          confirmButtonText: 'OK'
        })
      } else if (summary?.failed > 0) {
        const errorDetails = errors
          .map((entry) => entry.error)
          .filter(Boolean)
          .slice(0, 3)
          .join('\n')

        Swal.fire({
          icon: 'error',
          title: 'Request Failed',
          text: errorDetails || 'Failed to create stock request(s). Please try again.'
        })
        return
      } else {
        const referenceNumbers = created
          .map((transfer) => transfer.reference_number)
          .filter(Boolean)

        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: referenceNumbers.length
            ? `Stock request(s) submitted: ${referenceNumbers.join(', ')}`
            : `${summary?.successful || validItems.length} stock request(s) submitted successfully!`,
          confirmButtonText: 'OK'
        })
      }

      setItems([emptyItem()])
      setNotes('')
    } catch (error) {
      console.error('Error submitting stock request:', error)
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: parseApiError(error, 'Failed to submit stock request. Please try again.')
      })
    } finally {
      setLoading(false)
    }
  }

  const formDisabled = loading || fetchingData || !branchId || !branchContext?.warehouseId

  return (
    <>
      <div className="pagetitle">
        <h1>Request Stock</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/">Home</a></li>
            <li className="breadcrumb-item">Products</li>
            <li className="breadcrumb-item active">Request Stock</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">
                  <i className="bi bi-box-arrow-in-down me-2" />
                  Request Stock from Warehouse
                </h5>

                {!branchId && !fetchingData && (
                  <div className="alert alert-warning" role="alert">
                    You are not assigned to a branch. Stock requests cannot be submitted until a branch is assigned.
                  </div>
                )}

                {branchId && !branchContext?.warehouseId && !fetchingData && (
                  <div className="alert alert-warning" role="alert">
                    Your branch is not linked to a warehouse. Please contact your administrator.
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  <div className="row mb-3">
                    <label className="col-sm-2 col-form-label">Destination Branch</label>
                    <div className="col-sm-10">
                      <input
                        type="text"
                        className="form-control"
                        value={branchContext?.branchName || userInfo?.branch_name || 'Loading...'}
                        readOnly
                        disabled
                      />
                      <small className="text-muted">Automatically set to your assigned branch.</small>
                    </div>
                  </div>

                  <div className="row mb-3">
                    <label className="col-sm-2 col-form-label">Source Warehouse</label>
                    <div className="col-sm-10">
                      <input
                        type="text"
                        className="form-control"
                        value={branchContext?.warehouseName || userInfo?.warehouse_name || 'Loading...'}
                        readOnly
                        disabled
                      />
                      <small className="text-muted">Stock will be requested from your branch&apos;s linked warehouse.</small>
                    </div>
                  </div>

                  <div className="row mb-3">
                    <label className="col-sm-2 col-form-label">Products</label>
                    <div className="col-sm-10">
                      {items.map((item, index) => (
                        <div key={index} className="card mb-3">
                          <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <h6 className="card-title mb-0">Product {index + 1}</h6>
                              {items.length > 1 && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-danger"
                                  onClick={() => removeItem(index)}
                                  disabled={formDisabled}
                                >
                                  <i className="bi bi-trash" /> Remove
                                </button>
                              )}
                            </div>

                            <div className="row mb-2">
                              <label className="col-sm-3 col-form-label">
                                Product <span className="text-danger">*</span>
                              </label>
                              <div className="col-sm-9">
                                <Select
                                  value={
                                    item.product_id
                                      ? productOptions.find((opt) => opt.value === parseInt(item.product_id, 10))
                                      : null
                                  }
                                  onChange={(option) => handleItemSelectChange(index, option, 'product_id')}
                                  options={productOptions}
                                  isClearable
                                  isSearchable
                                  placeholder="Select Product"
                                  isDisabled={formDisabled}
                                  styles={selectStyles}
                                />
                              </div>
                            </div>

                            <div className="row mb-2">
                              <label className="col-sm-3 col-form-label">
                                Quantity <span className="text-danger">*</span>
                              </label>
                              <div className="col-sm-9">
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  className="form-control"
                                  value={item.quantity}
                                  onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                  placeholder="Enter quantity"
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>

                            <div className="row mb-2">
                              <label className="col-sm-3 col-form-label">
                                Reorder Level <span className="text-danger">*</span>
                              </label>
                              <div className="col-sm-9">
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  className="form-control"
                                  value={item.reorder_level}
                                  onChange={(e) => handleItemChange(index, 'reorder_level', e.target.value)}
                                  placeholder="Enter reorder trigger level"
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>

                         

                          

                            <div className="row">
                              <label className="col-sm-3 col-form-label">Notes</label>
                              <div className="col-sm-9">
                                <textarea
                                  className="form-control"
                                  rows="2"
                                  value={item.notes}
                                  onChange={(e) => handleItemChange(index, 'notes', e.target.value)}
                                  placeholder="Optional notes for this product"
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}

                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={addItem}
                        disabled={formDisabled}
                      >
                        <i className="bi bi-plus-circle me-2" />
                        Add Another Product
                      </button>
                    </div>
                  </div>

                  <div className="row mb-3">
                    <label htmlFor="request_notes" className="col-sm-2 col-form-label">Request Notes</label>
                    <div className="col-sm-10">
                      <textarea
                        className="form-control"
                        id="request_notes"
                        rows="3"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Optional notes for this stock request"
                        disabled={formDisabled}
                      />
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-sm-10 offset-sm-2">
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={formDisabled}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                            Submitting Request...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-send me-2" />
                            Submit Stock Request
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default CashierRequestStock
